import type { Resource, SecurityStep } from "../../../types";
import { PaywallAuthError, PaywallUnknownError } from "../paywall.errors";
import { SECURITY_STEPS } from "../../../constants";

// Pure function: Calculate next route based on security step
export const calculateNextRoute = (
  securityStep: SecurityStep,
  currentLocation: string,
  paths: {
    protected: string;
    profile: string;
    authorize: string;
    createProfile: string;
    noAccess: string;
  },
  hasResources: boolean
): string | null => {
  switch (securityStep) {
    case SECURITY_STEPS.AUTHORIZED:
      if (
        currentLocation !== paths.protected &&
        currentLocation !== paths.profile
      ) {
        return paths.protected;
      }
      break;

    case SECURITY_STEPS.AUTH_FAILED:
      if (currentLocation !== paths.authorize) {
        return paths.authorize;
      }
      break;

    case SECURITY_STEPS.PROFILE_NEEDED:
      if (currentLocation !== paths.createProfile) {
        return paths.createProfile;
      }
      break;

    case SECURITY_STEPS.CHECKING_RESOURCES:
      // Child will automatically send RESOURCE_ACCESS_UPDATED when ready
      // Prevent flash by blocking access to protected content during resource check
      if (currentLocation === paths.protected && !hasResources) {
        return paths.profile; // Redirect from protected path to profile while checking
      }
      break;

    case SECURITY_STEPS.NO_ACCESS:
      if (currentLocation !== paths.noAccess) {
        return paths.noAccess;
      }
      break;
  }
  return null;
};

// Pure function: Determine if paywall should be initialized
export const shouldInitializePaywall = (
  isReady: boolean,
  credentials: Record<string, string> | undefined,
  resources: Resource[]
): boolean => {
  return isReady && !!credentials && resources.length > 0;
};

// Pure function: Calculate loading text based on state
export const calculateLoadingText = (
  isReady: boolean,
  securityStep: SecurityStep,
  hideIframe?: boolean
): string | null => {
  if (hideIframe) {
    return null;
  }

  if (!isReady) {
    return "Loading...";
  }

  switch (securityStep) {
    case SECURITY_STEPS.CHECKING_AUTH:
      return "Authenticating...";

    case SECURITY_STEPS.CHECKING_RESOURCES:
      return "Checking access...";

    default:
      return null;
  }
};

// Pure function: Determine if content should be visible
export const calculateShouldShowContent = (
  isReady: boolean,
  securityStep: SecurityStep,
  hideIframe?: boolean
): boolean => {
  // Only show when properly authorized (paywall is always enabled when this component is used)
  if (!isReady) return false;
  if (hideIframe) return true;

  return securityStep === SECURITY_STEPS.AUTHORIZED;
};

// Pure function: Validate security step and return error if invalid
export const validateSecurityStep = (
  isReady: boolean,
  securityStep: SecurityStep
): Error | null => {
  if (!isReady) return null;

  switch (securityStep) {
    case SECURITY_STEPS.AUTH_FAILED:
      return new PaywallAuthError(
        "Authentication failed during paywall initialization"
      );

    case SECURITY_STEPS.CHECKING_AUTH:
    case SECURITY_STEPS.CHECKING_RESOURCES:
    case SECURITY_STEPS.PROFILE_NEEDED:
    case SECURITY_STEPS.NO_ACCESS:
    case SECURITY_STEPS.AUTHORIZED:
      // These are valid navigation/loading states, no error
      return null;

    default:
      // Unexpected security step - exhaustiveness check
      return new PaywallUnknownError(
        `Unexpected security step: ${securityStep}`
      );
  }
};
